import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

const EFFECTIVE_DATE = 'March 23, 2026';
const COMPANY_NAME = 'Portal Inc.';
const COMPANY_ADDRESS = 'Vancouver, British Columbia, Canada';
const CONTACT_EMAIL = 'legal@partyresume.com';

function Section({ number, title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-mono text-lg md:text-xl font-bold uppercase tracking-wider text-black mb-4">
        {number}. {title}
      </h2>
      <div className="font-mono text-sm leading-relaxed text-gray-800 space-y-3">
        {children}
      </div>
    </section>
  );
}

function SubSection({ number, title, children }) {
  return (
    <div className="ml-4 mb-4">
      <h3 className="font-mono text-sm font-bold uppercase tracking-wide text-black mb-2">
        {number} {title}
      </h3>
      <div className="font-mono text-sm leading-relaxed text-gray-800 space-y-2">
        {children}
      </div>
    </div>
  );
}

export default function TermsAndConditionsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b-2 border-black">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link
            to="/"
            className="font-mono text-lg font-bold uppercase tracking-wider text-black hover:opacity-70 transition-opacity"
          >
            Portal
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="font-mono text-xs uppercase tracking-wider text-gray-600 hover:text-black transition-colors"
          >
            Back
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-mono text-2xl md:text-3xl font-bold uppercase tracking-wider text-black mb-2">
          Terms and Conditions
        </h1>
        <p className="font-mono text-xs uppercase tracking-wider text-gray-500 mb-2">
          The Party Covenant
        </p>
        <p className="font-mono text-sm text-gray-600 mb-12">
          Effective Date: {EFFECTIVE_DATE}
        </p>

        {/* Preamble */}
        <div className="mb-12 p-6 border-2 border-black">
          <p className="font-mono text-sm leading-relaxed text-gray-800">
            Welcome to Portal. These Terms and Conditions (&quot;Terms&quot;) govern your access to
            and use of the Portal platform, including our website, mobile applications, and
            all related services (collectively, the &quot;Platform&quot;). By creating an account or using
            the Platform, you agree to be bound by these Terms. If you do not agree, do not use
            the Platform.
          </p>
        </div>

        {/* Section 1 */}
        <Section number={1} title="Definitions">
          <p>In these Terms, the following definitions apply:</p>
          <ul className="list-none space-y-2 ml-4">
            <li><strong>&quot;Host&quot;</strong> means a user who creates and manages events on the Platform.</li>
            <li><strong>&quot;Guest&quot;</strong> means a user who applies to attend events on the Platform.</li>
            <li><strong>&quot;Event&quot;</strong> means any gathering, party, or experience created by a Host on the Platform.</li>
            <li><strong>&quot;Wallet&quot;</strong> means a user&apos;s funds balance held within the Platform.</li>
            <li><strong>&quot;Greenlight&quot;</strong> means the Host&apos;s action to confirm an event will proceed, triggering the transfer of committed funds from Guest Wallets to the Host&apos;s Wallet.</li>
            <li><strong>&quot;Application&quot;</strong> means a Guest&apos;s request to attend an Event, including the associated commitment.</li>
            <li><strong>&quot;MIME&quot;</strong> (Make It More Epic) means a feature allowing Hosts to request additional commitments from accepted Guests.</li>
            <li><strong>&quot;Party Covenant&quot;</strong> means these Terms and Conditions.</li>
            <li><strong>&quot;Profile&quot;</strong> or <strong>&quot;Portal&quot;</strong> means the user profile created during onboarding, which Hosts may review when evaluating Applications.</li>
          </ul>
        </Section>

        {/* Section 2 */}
        <Section number={2} title="Eligibility and Account Registration">
          <SubSection number="2.1" title="Age Requirement">
            <p>
              You must be at least 19 years of age (or the age of majority in your jurisdiction,
              whichever is greater) to create an account and use the Platform. By registering, you
              represent and warrant that you meet this requirement.
            </p>
          </SubSection>
          <SubSection number="2.2" title="Account Security">
            <p>
              You are responsible for maintaining the confidentiality of your account credentials.
              You agree to notify us immediately of any unauthorized use of your account. We are
              not liable for any loss arising from unauthorized access to your account.
            </p>
          </SubSection>
          <SubSection number="2.3" title="Accurate Information">
            <p>
              You agree to provide accurate, current, and complete information during registration
              and to keep your profile information up to date. We reserve the right to suspend or
              terminate accounts containing false or misleading information.
            </p>
          </SubSection>
        </Section>

        {/* Section 3 */}
        <Section number={3} title="The Platform">
          <SubSection number="3.1" title="Purpose">
            <p>
              Portal is an event ticketing and crowdfunding platform. Hosts create events with
              funding goals, Guests apply and pledge funds to attend, and funds are transferred when
              the Host greenlights the event. The Platform facilitates connections between Hosts
              and Guests but does not itself organize, host, or attend any events.
            </p>
          </SubSection>
          <SubSection number="3.2" title="Platform Role">
            <p>
              Portal acts solely as an intermediary. We are not a party to any agreement
              between Hosts and Guests. We do not guarantee the quality, safety, legality, or
              occurrence of any event listed on the Platform.
            </p>
          </SubSection>
          <SubSection number="3.3" title="Modifications">
            <p>
              We reserve the right to modify, suspend, or discontinue any part of the Platform at
              any time, with or without notice. We will not be liable to you or any third party
              for any modification, suspension, or discontinuation.
            </p>
          </SubSection>
        </Section>

        {/* Section 4 */}
        <Section number={4} title="Wallet System">
          <SubSection number="4.1" title="Nature of Wallet Funds">
            <p>
              Funds added to your Wallet are held by the Platform for use exclusively within the
              Platform for event commitments and other Platform transactions. Wallet funds are not
              legal tender outside the Platform and may only be withdrawn under the conditions
              specified by the Platform&apos;s cash-out policy. Wallet funds are not a deposit, are
              not insured by any government agency, and do not accrue interest.
            </p>
          </SubSection>
          <SubSection number="4.2" title="Adding Funds">
            <p>
              You may add funds to your Wallet using real currency through our payment processor
              (Stripe). All purchases are processed in Canadian dollars (CAD) unless otherwise
              specified. Funds will be credited to your Wallet upon successful payment processing.
            </p>
          </SubSection>
          <SubSection number="4.3" title="Wallet Balance">
            <p>
              Your Wallet reflects your available balance. You are responsible for maintaining a
              sufficient balance to cover your commitments. The Platform displays balances in
              dollar format (e.g., $25.00).
            </p>
          </SubSection>
          <SubSection number="4.4" title="No Refunds on Wallet Funding">
            <p>
              All Wallet funding transactions are final and non-refundable, except as required by
              applicable law or as expressly permitted by the Platform&apos;s cash-out policy. You
              acknowledge that Wallet funds may lose utility if the Platform is discontinued.
            </p>
          </SubSection>
        </Section>

        {/* Section 5 */}
        <Section number={5} title="Event Transactions">
          <SubSection number="5.1" title="Event Creation">
            <p>
              Hosts may create events by providing event details, setting a ticket price, and
              defining a funding goal. Hosts are solely responsible for the accuracy of event
              information, including date, time, location, and description.
            </p>
          </SubSection>
          <SubSection number="5.2" title="Guest Applications">
            <p>
              To apply for an event, Guests must commit funds from their Wallet equal to the ticket
              price. These funds are held as a commitment (escrow) and deducted from the Guest&apos;s
              available balance immediately upon application.
            </p>
          </SubSection>
          <SubSection number="5.3" title="Application Review">
            <p>
              In curated mode, Hosts review and accept or decline Guest Applications at their
              discretion. In open mode, Applications are automatically accepted. Hosts may review
              Guest Profiles as part of their decision-making process.
            </p>
          </SubSection>
          <SubSection number="5.4" title="Escrow and Greenlight">
            <p>
              Committed funds are held in escrow within the Platform until the Host greenlights
              the event. Upon greenlighting, committed funds from accepted Guests are transferred
              to the Host&apos;s Wallet, less any applicable platform fees. The escrow mechanism is an
              internal accounting function of the Platform and does not constitute a trust, escrow
              account, or fiduciary arrangement under applicable law.
            </p>
            <p className="text-xs text-gray-500 italic">
              [Note: This section is pending legal review for compliance with British Columbia
              financial services regulations.]
            </p>
          </SubSection>
          <SubSection number="5.5" title="Refunds and Cancellations">
            <p>
              If a Host cancels an event or declines a Guest&apos;s Application, the committed funds
              are refunded to the Guest&apos;s Wallet. If a Guest withdraws their Application before
              the event is greenlit, committed funds are refunded minus any applicable flake fees
              as described in Section 6.
            </p>
          </SubSection>
          <SubSection number="5.6" title="Location Disclosure">
            <p>
              Event locations are revealed only to accepted, paid Guests after the event is
              greenlit. This information is confidential and must not be shared with non-attendees.
            </p>
          </SubSection>
        </Section>

        {/* Section 6 */}
        <Section number={6} title="Fees">
          <SubSection number="6.1" title="Platform Fees">
            <p>
              Portal may charge a platform fee on event transactions, calculated as a
              percentage of the funds transferred upon greenlighting. The current fee schedule is
              available on the Platform and may be updated from time to time.
            </p>
          </SubSection>
          <SubSection number="6.2" title="Flake Fees">
            <p>
              Guests who withdraw from an accepted Application after a specified period may be
              subject to a flake fee, which is deducted from their refunded funds. Flake fees
              compensate Hosts for lost spots and are credited to the Host&apos;s Wallet.
            </p>
          </SubSection>
          <SubSection number="6.3" title="Payment Processing Fees">
            <p>
              Third-party payment processing fees (e.g., Stripe fees) apply when adding funds to
              your Wallet and are borne by the user. These fees are non-refundable.
            </p>
          </SubSection>
        </Section>

        {/* Section 7 */}
        <Section number={7} title="Assumption of Risk">
          <p>
            By using the Platform and attending events discovered through the Platform, you
            acknowledge and agree to the following:
          </p>
          <ul className="list-none space-y-3 ml-4 mt-3">
            <li>
              <strong>7.1</strong> Events listed on the Platform are organized and operated by
              independent Hosts, not by Portal. We exercise no control over the conduct,
              safety, or legality of events or their attendees.
            </li>
            <li>
              <strong>7.2</strong> Attendance at any event is at your own risk. You voluntarily
              assume all risks associated with attending events, including but not limited to
              risks of personal injury, property damage, illness, or death.
            </li>
            <li>
              <strong>7.3</strong> You are solely responsible for your own conduct and safety
              at events. You agree to comply with all applicable laws, venue rules, and any
              instructions provided by the Host.
            </li>
            <li>
              <strong>7.4</strong> Portal is not responsible for verifying the identity,
              background, or suitability of any Host, Guest, or event attendee.
            </li>
            <li>
              <strong>7.5</strong> You release Portal, its officers, directors, employees,
              and agents from any and all claims, liabilities, damages, or losses arising from
              your attendance at or participation in any event.
            </li>
          </ul>
          <p className="text-xs text-gray-500 italic mt-3">
            [Note: This section is pending legal review regarding enforceability under British
            Columbia consumer protection law.]
          </p>
        </Section>

        {/* Section 8 */}
        <Section number={8} title="MIME (Make It More Epic)">
          <SubSection number="8.1" title="MIME Rounds">
            <p>
              Hosts may initiate MIME rounds, requesting additional commitments from accepted
              Guests to enhance the event experience. MIME rounds include a deadline (shot clock)
              by which Guests must respond.
            </p>
          </SubSection>
          <SubSection number="8.2" title="Guest Response">
            <p>
              Guests must accept or decline MIME requests before the deadline expires. Failure to
              respond may result in automatic removal from the event at the Host&apos;s discretion,
              depending on the MIME round settings.
            </p>
          </SubSection>
          <SubSection number="8.3" title="MIME Commitments">
            <p>
              Accepted MIME commitments are subject to the same escrow and greenlight process as
              initial Application commitments described in Section 5.
            </p>
          </SubSection>
        </Section>

        {/* Section 9 */}
        <Section number={9} title="User Conduct">
          <p>You agree not to:</p>
          <ul className="list-none space-y-2 ml-4 mt-2">
            <li>(a) Use the Platform for any unlawful purpose or in violation of these Terms;</li>
            <li>(b) Create events that are fraudulent, misleading, or that violate applicable laws;</li>
            <li>(c) Harass, threaten, or intimidate other users;</li>
            <li>(d) Share event locations or confidential event details with non-attendees;</li>
            <li>(e) Manipulate or abuse the Wallet system, including creating multiple accounts to exploit bonuses or promotions;</li>
            <li>(f) Impersonate any person or entity or misrepresent your affiliation;</li>
            <li>(g) Interfere with or disrupt the Platform or its infrastructure;</li>
            <li>(h) Use automated means to access the Platform without our express permission;</li>
            <li>(i) Upload content that is defamatory, obscene, or infringes on the rights of others.</li>
          </ul>
        </Section>

        {/* Section 10 */}
        <Section number={10} title="User Content">
          <SubSection number="10.1" title="Ownership">
            <p>
              You retain ownership of content you upload to the Platform, including profile
              information, photos, and event descriptions (&quot;User Content&quot;).
            </p>
          </SubSection>
          <SubSection number="10.2" title="License Grant">
            <p>
              By uploading User Content, you grant Portal a non-exclusive, worldwide,
              royalty-free, sublicensable license to use, display, reproduce, and distribute your
              User Content in connection with operating and promoting the Platform.
            </p>
          </SubSection>
          <SubSection number="10.3" title="Content Moderation">
            <p>
              We reserve the right to remove any User Content that violates these Terms or that
              we deem inappropriate, without prior notice.
            </p>
          </SubSection>
        </Section>

        {/* Section 11 */}
        <Section number={11} title="Privacy">
          <SubSection number="11.1" title="Data Collection">
            <p>
              Your use of the Platform is subject to our Privacy Policy, which describes how we
              collect, use, and share your personal information. By using the Platform, you
              consent to our data practices as described in the Privacy Policy.
            </p>
          </SubSection>
          <SubSection number="11.2" title="Profile Visibility">
            <p>
              When you apply to an event, your Portal Page (profile) may be shared with the Host
              for application review purposes. You can control certain privacy settings, including
              incognito mode and shadow location, through your profile settings.
            </p>
          </SubSection>
          <SubSection number="11.3" title="Friend Connections">
            <p>
              The Platform includes social features such as friend requests and friend suggestions.
              Your profile visibility to other users is governed by your privacy settings.
            </p>
          </SubSection>
        </Section>

        {/* Section 12 */}
        <Section number={12} title="Intellectual Property">
          <p>
            The Platform, including its design, code, branding, trademarks, and all related
            intellectual property, is owned by {COMPANY_NAME} and protected by applicable
            intellectual property laws. You may not copy, modify, distribute, or create derivative
            works based on the Platform without our express written consent.
          </p>
        </Section>

        {/* Section 13 */}
        <Section number={13} title="Disclaimers">
          <SubSection number="13.1" title="As-Is Basis">
            <p>
              THE PLATFORM IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY
              KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES
              OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </SubSection>
          <SubSection number="13.2" title="No Guarantee">
            <p>
              WE DO NOT GUARANTEE THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR
              SECURE. WE DO NOT GUARANTEE THE ACCURACY OR COMPLETENESS OF ANY INFORMATION
              ON THE PLATFORM, INCLUDING EVENT LISTINGS.
            </p>
          </SubSection>
          <SubSection number="13.3" title="Third-Party Services">
            <p>
              The Platform integrates with third-party services (including Stripe for payment
              processing). We are not responsible for the availability, accuracy, or practices
              of these third-party services.
            </p>
          </SubSection>
        </Section>

        {/* Section 14 */}
        <Section number={14} title="Charitable and Community Events">
          <SubSection number="14.1" title="Charitable Events">
            <p>
              Hosts may designate events as charitable or community-oriented. Portal does
              not verify the charitable status of any event or Host. Users are responsible for
              conducting their own due diligence regarding the legitimacy and charitable status
              of any event.
            </p>
          </SubSection>
          <SubSection number="14.2" title="No Tax Receipts">
            <p>
              Transactions on the Platform do not constitute charitable donations for tax
              purposes. Portal does not issue tax receipts for any transactions on the
              Platform.
            </p>
          </SubSection>
          <SubSection number="14.3" title="Compliance">
            <p>
              Hosts organizing charitable or fundraising events are solely responsible for
              compliance with all applicable laws, including but not limited to charitable
              solicitation laws and regulations.
            </p>
          </SubSection>
          <p className="text-xs text-gray-500 italic">
            [Note: This section is pending legal review regarding charitable solicitation
            regulations in British Columbia and Canada.]
          </p>
        </Section>

        {/* Section 15 */}
        <Section number={15} title="Limitation of Liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL {COMPANY_NAME},
            ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF
            PROFITS, DATA, OR USE, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE PLATFORM,
            WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY. OUR TOTAL
            LIABILITY SHALL NOT EXCEED THE AMOUNT OF FUNDS YOU ADDED TO YOUR WALLET IN THE TWELVE (12) MONTHS
            PRECEDING THE CLAIM.
          </p>
        </Section>

        {/* Section 16 */}
        <Section number={16} title="Indemnification">
          <p>
            You agree to indemnify, defend, and hold harmless {COMPANY_NAME} and its officers,
            directors, employees, and agents from and against any claims, liabilities, damages,
            losses, costs, or expenses (including reasonable legal fees) arising out of or
            related to: (a) your use of the Platform; (b) your violation of these Terms;
            (c) your violation of any rights of another user or third party; or (d) any event
            you host or attend through the Platform.
          </p>
        </Section>

        {/* Section 17 */}
        <Section number={17} title="Termination">
          <SubSection number="17.1" title="By You">
            <p>
              You may terminate your account at any time through your account settings. Upon
              termination, any remaining Wallet balance may be forfeited unless a withdrawal is
              processed in accordance with our then-current policies.
            </p>
          </SubSection>
          <SubSection number="17.2" title="By Us">
            <p>
              We may suspend or terminate your account at any time, with or without cause, and
              with or without notice. Reasons for termination include but are not limited to
              violation of these Terms, fraudulent activity, or conduct harmful to other users
              or the Platform.
            </p>
          </SubSection>
          <SubSection number="17.3" title="Effect of Termination">
            <p>
              Upon termination, your right to use the Platform ceases immediately. Sections of
              these Terms that by their nature should survive termination shall survive,
              including but not limited to Sections 7, 13, 15, 16, and 18.
            </p>
          </SubSection>
        </Section>

        {/* Section 18 */}
        <Section number={18} title="Governing Law and Dispute Resolution">
          <SubSection number="18.1" title="Governing Law">
            <p>
              These Terms are governed by and construed in accordance with the laws of the
              Province of British Columbia, Canada, without regard to its conflict of law
              provisions.
            </p>
          </SubSection>
          <SubSection number="18.2" title="Jurisdiction">
            <p>
              You agree to submit to the exclusive jurisdiction of the courts located in
              Vancouver, British Columbia, Canada, for the resolution of any disputes arising
              out of or relating to these Terms or the Platform.
            </p>
          </SubSection>
          <SubSection number="18.3" title="Informal Resolution">
            <p>
              Before filing any formal legal action, you agree to first contact us at{' '}
              {CONTACT_EMAIL} and attempt to resolve the dispute informally for at least thirty
              (30) days.
            </p>
          </SubSection>
        </Section>

        {/* Section 19 */}
        <Section number={19} title="Changes to These Terms">
          <p>
            We may update these Terms from time to time. If we make material changes, we will
            notify you by posting the updated Terms on the Platform and updating the effective
            date. Your continued use of the Platform after such changes constitutes acceptance
            of the revised Terms. We encourage you to review these Terms periodically.
          </p>
        </Section>

        {/* Section 20 */}
        <Section number={20} title="General Provisions">
          <SubSection number="20.1" title="Entire Agreement">
            <p>
              These Terms, together with the Privacy Policy, constitute the entire agreement
              between you and {COMPANY_NAME} regarding your use of the Platform.
            </p>
          </SubSection>
          <SubSection number="20.2" title="Severability">
            <p>
              If any provision of these Terms is found to be unenforceable, the remaining
              provisions shall continue in full force and effect.
            </p>
          </SubSection>
          <SubSection number="20.3" title="Waiver">
            <p>
              Our failure to enforce any right or provision of these Terms shall not constitute
              a waiver of such right or provision.
            </p>
          </SubSection>
          <SubSection number="20.4" title="Assignment">
            <p>
              You may not assign your rights or obligations under these Terms without our prior
              written consent. We may assign our rights and obligations without restriction.
            </p>
          </SubSection>
        </Section>

        {/* Section 21 */}
        <Section number={21} title="Contact Us">
          <p>
            If you have questions about these Terms, please contact us at:
          </p>
          <div className="mt-3 p-4 border-2 border-black">
            <p className="font-bold">{COMPANY_NAME}</p>
            <p>{COMPANY_ADDRESS}</p>
            <p>{CONTACT_EMAIL}</p>
          </div>
        </Section>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t-2 border-black">
          <p className="font-mono text-xs text-gray-500 text-center uppercase tracking-wider">
            Portal &mdash; Find Your Party. Find Your People.
          </p>
        </div>
      </main>
    </div>
  );
}
